import { useEffect, useState } from "react";
import {
  Newspaper,
  CircleNotch,
  Plus,
  PencilSimple,
  Trash,
  FloppyDisk,
  X,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  listNews,
  devCreateNews,
  devUpdateNews,
  devDeleteNews,
  getGameVersion,
  devSetGameVersion,
} from "@/lib/api";
import { toast } from "sonner";

/**
 * NewsAdminDialog — dev-only CRUD for player-facing announcements + the
 * editable Game Version string that surfaces under the Logout row in the
 * profile dropdown.
 *
 * Layout:
 *   ┌─ Game Version editor (small strip at the top)
 *   ├─ "New Post" button + editor form
 *   └─ Existing post list (edit / delete inline)
 *
 * Opened from the "News" button in the Developer Panel header.
 */
export default function NewsAdminDialog({ open, onOpenChange }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState("");
  const [versionDirty, setVersionDirty] = useState(false);
  const [versionSaving, setVersionSaving] = useState(false);
  const [editing, setEditing] = useState(null); // { id?, title, body }
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    setLoading(true);
    return Promise.all([
      listNews().then((data) => setPosts(Array.isArray(data) ? data : [])),
      getGameVersion().then((r) => {
        setVersion(r?.version || "");
        setVersionDirty(false);
      }),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    refresh();
    setEditing(null);
  }, [open]);

  const startCreate = () => setEditing({ title: "", body: "" });
  const startEdit = (p) => setEditing({ id: p.id, title: p.title, body: p.body });
  const cancelEdit = () => setEditing(null);

  const savePost = async () => {
    if (!editing) return;
    const title = (editing.title || "").trim();
    const body = (editing.body || "").trim();
    if (!title || !body) {
      toast.error("Both title and body are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await devUpdateNews(editing.id, { title, body });
        toast.success("Post updated.");
      } else {
        await devCreateNews({ title, body });
        toast.success("Post published.");
      }
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save post.");
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (pid) => {
    if (!confirm("Delete this post? This cannot be undone.")) return;
    try {
      await devDeleteNews(pid);
      toast.success("Post deleted.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete post.");
    }
  };

  const saveVersion = async () => {
    const v = (version || "").trim();
    if (!v) {
      toast.error("Version cannot be empty.");
      return;
    }
    setVersionSaving(true);
    try {
      await devSetGameVersion(v);
      setVersionDirty(false);
      toast.success(`Game version set to ${v}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save version.");
    } finally {
      setVersionSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="news-admin-dialog"
        className="rounded-none border border-amber-500/30 bg-[#0A0E14] text-white max-w-3xl p-0 max-h-[90vh] grid grid-rows-[auto_minmax(0,1fr)] gap-0"
      >
        <DialogHeader className="px-5 py-3 border-b border-amber-500/20 bg-amber-500/[0.04]">
          <DialogTitle className="mono uppercase tracking-widest text-[12px] text-amber-300 flex items-center gap-2">
            <Newspaper size={14} weight="fill" /> News CMS
            <span className="ml-2 text-zinc-500 text-[10px] tracking-wider font-normal">
              Player-facing announcements + Game Version
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          {/* Game Version strip */}
          <div className="border border-white/10 bg-black/30 px-3 py-3">
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
              Game Version (shown under Logout to all players)
            </div>
            <div className="flex items-center gap-2">
              <Input
                data-testid="admin-game-version-input"
                value={version}
                onChange={(e) => { setVersion(e.target.value); setVersionDirty(true); }}
                maxLength={40}
                placeholder="v1.0.0"
                className="rounded-none h-8 bg-black/40 border-white/10 mono text-[11px]"
              />
              <Button
                data-testid="admin-save-game-version"
                size="sm"
                onClick={saveVersion}
                disabled={!versionDirty || versionSaving}
                className="rounded-none h-8 mono uppercase text-[10px] tracking-widest bg-amber-500 hover:bg-amber-400 text-black"
              >
                {versionSaving ? <CircleNotch size={11} className="animate-spin mr-1.5" /> : <FloppyDisk size={11} className="mr-1.5" />}
                Save
              </Button>
            </div>
          </div>

          {/* Editor */}
          {editing ? (
            <div
              data-testid="news-post-editor"
              className="border border-sky-500/30 bg-sky-500/[0.03] px-3 py-3 space-y-2"
            >
              <div className="mono text-[10px] uppercase tracking-widest text-sky-300">
                {editing.id ? "Edit Post" : "New Post"}
              </div>
              <Input
                data-testid="news-post-title"
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="Title (max 140 chars)"
                maxLength={140}
                className="rounded-none h-8 bg-black/40 border-white/10 mono text-[11px]"
              />
              <Textarea
                data-testid="news-post-body"
                value={editing.body}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                placeholder="Body (Markdown-friendly plain text; blank lines preserved)"
                maxLength={8000}
                rows={8}
                className="rounded-none bg-black/40 border-white/10 text-[12px] leading-relaxed"
              />
              <div className="flex items-center gap-2 justify-end">
                <Button
                  data-testid="news-post-cancel"
                  size="sm"
                  variant="outline"
                  onClick={cancelEdit}
                  className="rounded-none h-8 mono uppercase text-[10px] tracking-widest border-white/10 hover:bg-white/5"
                >
                  <X size={11} className="mr-1.5" /> Cancel
                </Button>
                <Button
                  data-testid="news-post-save"
                  size="sm"
                  onClick={savePost}
                  disabled={saving}
                  className="rounded-none h-8 mono uppercase text-[10px] tracking-widest bg-sky-500 hover:bg-sky-400 text-black"
                >
                  {saving ? <CircleNotch size={11} className="animate-spin mr-1.5" /> : <FloppyDisk size={11} className="mr-1.5" />}
                  {editing.id ? "Save Changes" : "Publish"}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              data-testid="news-new-post-btn"
              size="sm"
              onClick={startCreate}
              className="rounded-none h-8 mono uppercase text-[10px] tracking-widest bg-sky-500 hover:bg-sky-400 text-black"
            >
              <Plus size={11} className="mr-1.5" /> New Post
            </Button>
          )}

          {/* Post list */}
          <div className="space-y-2">
            {loading ? (
              <div className="flex items-center justify-center py-10 text-zinc-500 mono text-[11px] uppercase tracking-widest">
                <CircleNotch size={14} className="animate-spin mr-2" /> Loading…
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-10 mono text-[11px] uppercase tracking-widest text-zinc-500">
                No posts yet.
              </div>
            ) : (
              posts.map((p) => (
                <div
                  key={p.id}
                  data-testid={`admin-news-row-${p.id}`}
                  className="border border-white/10 bg-black/30 px-3 py-2 flex items-start gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sky-200 text-[12px] truncate">{p.title}</div>
                    <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      {new Date(p.created_at).toLocaleString()}
                      {p.author_name && <> · {p.author_name}</>}
                    </div>
                    <div className="text-zinc-400 text-[11px] mt-1 line-clamp-2">
                      {p.body}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      data-testid={`admin-news-edit-${p.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => startEdit(p)}
                      className="rounded-none h-7 mono uppercase text-[10px] tracking-widest border-white/10 hover:bg-white/5"
                    >
                      <PencilSimple size={11} />
                    </Button>
                    <Button
                      data-testid={`admin-news-delete-${p.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => deletePost(p.id)}
                      className="rounded-none h-7 mono uppercase text-[10px] tracking-widest border-red-500/30 text-red-300 hover:bg-red-500/10"
                    >
                      <Trash size={11} />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
